<template>
  <table
    role="presentation"
    cellpadding="0"
    cellspacing="0"
    border="0"
    style="border-collapse: collapse;"
    :style="{ 'font-family': data.font }"
  >
    <tbody>
      <tr>
        <td align="left" style="padding: 0;">
          <div
            v-if="data.displayName"
            style="font-style: normal; font-weight: 600; line-height: normal;"
          >
            <span
              :style="{ fontSize: useFontSizeScale(16).value }"
              >{{ data.displayName }}</span
            >
          </div>
        </td>
      </tr>

      <tr>
        <td align="left" style="padding: 0;">
          <div
            v-if="data.profession"
            style="font-style: normal; font-weight: 500; line-height: 1.5; margin-top: 12px; word-break: break-all;"
          >
            <span
              :style="{ fontSize: useFontSizeScale(12).value }"
              >{{ data.profession }}</span
            >
          </div>
        </td>
      </tr>

      <tr>
        <td align="left" style="padding: 0;">
          <div
            v-if="data.company"
            style="border-radius: 12px; font-style: normal; font-weight: 500; line-height: 1.5; word-break: break-all;"
          >
            <span
              :style="{ fontSize: useFontSizeScale(12).value }"
              >{{ data.company }}</span
            >
          </div>
        </td>
      </tr>

      <tr>
        <td align="left" style="padding: 0;">
          <div
            v-if="data.telephoneNumber"
            style="border-radius: 12px; font-style: normal; font-weight: 500; line-height: 1.5; margin-top: 12px; word-break: break-all;"
          >
            <a
              v-if="data.telephoneNumber"
              :href="'tel:' + data.telephoneNumber"
              :style="{ fontSize: useFontSizeScale(12).value }"
              style="color: inherit; text-decoration: none;"
              >{{ data.telephoneNumber }}</a
            >
          </div>
        </td>
      </tr>

      <tr>
        <td align="left" style="padding: 0;">
          <div
            v-if="data.email"
            style="border-radius: 12px; font-style: normal; font-weight: 500; line-height: 1.5; word-break: break-all;"
          >
            <a
              :href="'mailto:' + data.email"
              :style="{ fontSize: useFontSizeScale(12).value }"
              style="color: inherit; text-decoration: none;"
              >{{ data.email }}</a
            >
          </div>
        </td>
      </tr>

      <tr>
        <td align="left" style="padding: 0;">
          <div
            v-if="data.website"
            style="border-radius: 12px; font-style: normal; font-weight: 500; line-height: 1.5; word-break: break-all;"
          >
            <a
              v-if="data.website"
              target="_blank"
              :href="data.website.startsWith('http') ? data.website : 'https://' + data.website"
              :style="{ fontSize: useFontSizeScale(12).value }"
              style="color: inherit; text-decoration: none;"
              >{{ data.website }}</a
            >
          </div>
        </td>
      </tr>

      <tr>
        <td align="left" style="padding: 0;">
          <table
            role="presentation"
            cellpadding="0"
            cellspacing="0"
            border="0"
            style="border-collapse: collapse; margin-top: 12px;"
          >
            <tbody>
              <tr v-if="data.instagram || data.facebook || data.linkedin || data.youtube || data.x">
                <td align="left" style="padding-right: 12px;">
                  <a
                    tabindex="-1"
                    v-if="data.instagram && data.instagramIconUrl"
                    :href="'https://www.instagram.com/' + data.instagram"
                    target="_blank"
                    style="display: inline-block; text-decoration: none; width: 16px; height: 16px;"
                  >
                    <img
                      :src="data.instagramIconUrl"
                      alt="Instagram"
                      width="16"
                      height="16"
                      style="display: inline-block;"
                    />
                  </a>
                </td>

                <td align="left" style="padding-right: 12px;">
                  <a
                    tabindex="-1"
                    v-if="data.facebook && data.facebookIconUrl"
                    :href="'https://www.facebook.com/' + data.facebook"
                    target="_blank"
                    style="display: inline-block; text-decoration: none; width: 16px; height: 16px;"
                  >
                    <img
                      :src="data.facebookIconUrl"
                      alt="Facebook"
                      width="16"
                      height="16"
                      style="display:inline-block;"
                    />
                  </a>
                </td>

                <td align="left" style="padding-right: 12px;">
                  <a
                    tabindex="-1"
                    v-if="data.linkedin && data.linkedinIconUrl"
                    :href="'https://www.linkedin.com/in/' + data.linkedin"
                    target="_blank"
                    style="display: inline-block; text-decoration: none; width: 16px; height: 16px;"
                  >
                    <img
                      :src="data.linkedinIconUrl"
                      alt="LinkedIn"
                      width="16"
                      height="16"
                      style="display:inline-block;"
                    />
                  </a>
                </td>

                <td align="left" style="padding-right: 12px;">
                  <a
                    tabindex="-1"
                    v-if="data.youtube && data.youtubeIconUrl"
                    :href="'https://www.youtube.com/@' + data.youtube"
                    target="_blank"
                    style="display: inline-block; text-decoration: none; width: 16px; height: 16px;"
                  >
                    <img
                      :src="data.youtubeIconUrl"
                      alt="YouTube"
                      width="16"
                      height="16"
                      style="display:inline-block;"
                    />
                  </a>
                </td>

                <td align="left" style="padding-right: 12px;">
                  <a
                    tabindex="-1"
                    v-if="data.x && data.xIconUrl"
                    :href="'https://www.x.com/' + data.x"
                    target="_blank"
                    style="display: inline-block; text-decoration: none; width: 16px; height: 16px;"
                  >
                    <img
                      :src="data.xIconUrl"
                      alt="X (Twitter)"
                      width="16"
                      height="16"
                      style="display:inline-block;"
                    />
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
import { useFontSizeScale } from '@/io.ox/mail/settings/signatures/signature-designer/composables/useFontSizeScale'

const availableFields = [
  'displayName',
  'profession',
  'company',
  'telephoneNumber',
  'email',
  'website',
  'facebook',
  'instagram',
  'youtube',
  'linkedin',
  'x',
  'font',
  'fontSize'
]

defineProps({
  data: { type: Object, required: true }
})

defineExpose({
  availableFields,
  previewTheme: { text: 'black' }
})
</script>
